import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Globe,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  Shield,
  Zap,
  FileText,
  Monitor,
  Eye,
  ChevronDown,
  ChevronUp,
  Info,
  Lock,
  ExternalLink
} from 'lucide-react';
import { transformResults } from '../../utils/seoAuditHelpers';

const API_BASE =
  process.env.REACT_APP_SEO_AUDIT_API ||
  'https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev';

// ─── Score helpers ──────────────────────────────────────────────────────
const getScoreColor = (score) => {
  if (score >= 80) return '#22c55e';
  if (score >= 50) return '#eab308';
  return '#ef4444';
};

const getScoreBg = (score) => {
  if (score >= 80) return 'rgba(34,197,94,0.08)';
  if (score >= 50) return 'rgba(234,179,8,0.08)';
  return 'rgba(239,68,68,0.08)';
};

const getPriorityStyle = (priority) => {
  switch (priority) {
    case 'critical':
      return { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' };
    case 'high':
      return { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' };
    case 'medium':
      return { bg: '#fefce8', text: '#ca8a04', border: '#fef08a' };
    case 'warning':
      return { bg: '#fefce8', text: '#ca8a04', border: '#fef08a' };
    case 'low':
      return { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' };
    case 'info':
      return { bg: '#f0f9ff', text: '#0284c7', border: '#bae6fd' };
    default:
      return { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' };
  }
};

const WHY_IT_MATTERS = {
  'Title Too Short':
    'Short titles miss valuable keywords and fail to fully describe your page, reducing click-through rates.',
  'Title Too Long':
    'Titles over 60 characters get truncated in search results, hiding important keywords.',
  'Missing Page Title':
    'Pages without titles cannot be properly indexed or displayed in search results.',
  'Missing Meta Description':
    'Without a meta description, search engines auto-generate snippets that may not represent your page well.',
  'Missing Image Alt Text':
    'Alt text improves accessibility for visually impaired users and helps search engines understand image content.',
  'Missing Image Titles':
    'Image title attributes provide additional context on hover and can contribute to image search rankings.',
  'Render Blocking Resources':
    'Render-blocking CSS and JS delay how quickly users see your page, increasing bounce rates.',
  'Low Content Rate':
    'Pages with a low text-to-HTML ratio may be seen as thin content by search engines.',
  'High Content Rate':
    'An unusually high text ratio can indicate poor HTML structure which affects parsing by crawlers.',
  'Low Character Count':
    'Very short pages may not provide enough value to rank competitively for target keywords.',
  'High Character Count':
    'Excessively long pages without clear structure can overwhelm users and reduce engagement.',
  'Low Readability':
    'Hard-to-read content reduces time on page and engagement, indirectly affecting search rankings.',
  'Non-SEO Friendly URL':
    'Clean, descriptive URLs improve click-through rates and help search engines understand page topics.',
  'Missing H1 Tag':
    'The H1 tag is the primary heading signal for search engines to understand what a page is about.',
  'Broken Internal Links':
    'Broken links waste crawl budget, hurt user experience, and pass zero link equity.',
  'Missing Sitemap':
    'Without a sitemap, search engines may miss important pages on your site during crawling.',
  'Missing robots.txt':
    'Robots.txt controls which pages crawlers access \u2014 without it, sensitive pages may be indexed.',
  'HTTPS Not Enabled':
    'HTTPS is a confirmed ranking factor and essential for user trust and data security.',
  'Redirect Loops':
    'Redirect loops prevent pages from loading entirely, making them inaccessible to users and crawlers.',
  'Non-Indexable Pages':
    'If important pages are blocked from indexing, they cannot appear in search results at all.',
  'Duplicate Title Tags':
    'Duplicate titles confuse search engines about which page to rank for a given query.',
  'Duplicate Meta Descriptions':
    'Duplicate descriptions reduce the uniqueness signal and may lead to poor snippet selection.',
  'Duplicate Content':
    'Duplicate content splits ranking signals between pages, diluting their individual authority.',
  'Broken Resources':
    'Missing CSS, JS, or images cause visual and functional issues that increase bounce rates.',
  'Server Signature Exposed':
    'Exposing server version information makes it easier for attackers to target known vulnerabilities.',
  'Schema Markup Errors':
    'Structured data errors prevent rich snippets from appearing in search results.',
  'Missing Doctype':
    'A missing doctype forces browsers into quirks mode, causing inconsistent rendering.',
  'Missing Encoding Meta Tag':
    'Without character encoding, browsers may misinterpret special characters.',
  'Mixed Content':
    'Mixed HTTP/HTTPS content triggers browser security warnings that scare away visitors.',
  'Page Size Too Large':
    'Large page sizes slow loading times, especially on mobile, hurting Core Web Vitals.',
  'Meta Refresh Redirect':
    'Meta refresh redirects are slower than server-side redirects and can confuse search engines.',
  'Deprecated HTML Tags':
    'Deprecated tags may not render correctly in modern browsers and signal outdated code.',
  'Duplicate Meta Tags':
    'Multiple identical meta tags confuse crawlers and may cause unpredictable indexing.',
  'Duplicate Title':
    'Pages sharing the same title compete against each other in search results.',
  'Missing Favicon':
    'A favicon improves brand recognition in browser tabs and bookmarks.',
  'Flash Content':
    'Flash is unsupported by modern browsers and search engines, making content invisible.',
  'Lorem Ipsum Detected':
    'Placeholder text signals an unfinished page, which can hurt credibility and rankings.',
  'Recursive Canonical':
    'Incorrect self-referencing canonicals can create crawl loops and waste crawl budget.',
  'Canonical Chain':
    'Chained canonicals dilute the signal and may be ignored by search engines.',
  'Canonical to Broken Page':
    "Pointing a canonical to a broken URL wastes the page's ranking potential entirely.",
  'Links to Redirects':
    'Internal links to redirecting URLs add latency and waste crawl budget.',
  'Orphan Page':
    'Pages with no internal links are hard for search engines to discover and rarely rank well.',
  'Redirect Chain':
    'Each extra redirect adds load time and risks the chain being truncated by crawlers.',
  'Irrelevant Description':
    "A meta description that doesn't match content leads to poor click-through rates.",
  'Irrelevant Title':
    'A misleading title causes users to leave quickly, sending negative engagement signals.',
  'Irrelevant Meta Keywords':
    'While meta keywords are largely ignored, irrelevant ones can signal low-quality optimization.',
  'Frame/IFrame Content':
    'Content within frames is harder for search engines to index and may not pass link equity.'
};

const getCategoryIcon = (name) => {
  const iconProps = { size: 22, strokeWidth: 2 };
  switch (name) {
    case 'Technical':
      return <Monitor {...iconProps} />;
    case 'On-Page SEO':
      return <FileText {...iconProps} />;
    case 'Performance':
      return <Zap {...iconProps} />;
    case 'Security':
      return <Shield {...iconProps} />;
    case 'Accessibility':
      return <Eye {...iconProps} />;
    default:
      return <Globe {...iconProps} />;
  }
};

// ─── Component ──────────────────────────────────────────────────────────
const SeoPublicReport = () => {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [psiStrategy, setPsiStrategy] = useState('mobile');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [expandedIssues, setExpandedIssues] = useState({});

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await fetch(`${API_BASE}/audit/report/${reportId}`);
        if (!res.ok) {
          throw new Error(
            res.status === 404 ? 'Report not found' : 'Failed to load report'
          );
        }
        const data = await res.json();
        setReport(transformResults(data.results || data));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [reportId]);

  // ── Loading State ──
  if (loading) {
    return (
      <div style={styles.centerPage}>
        <Loader2
          size={48}
          style={{ animation: 'spin 1s linear infinite', color: '#3766a8' }}
        />
        <p
          style={{
            marginTop: 16,
            color: '#64748b',
            fontSize: 18,
            fontWeight: 500
          }}>
          Loading report...
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  // ── Error State ──
  if (error) {
    return (
      <div style={styles.centerPage}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <XCircle size={64} color="#ef4444" />
          <h1
            style={{
              fontSize: 24,
              fontWeight: 700,
              marginTop: 16,
              color: '#1e293b'
            }}>
            {error}
          </h1>
          <p style={{ color: '#64748b', marginTop: 8 }}>
            This report link may be invalid or expired.
          </p>
        </div>
      </div>
    );
  }

  const data = report;
  const scores = data?.rawScores || {};
  const issues = data?.recommendations || [];
  const psi = data?.psi || {};
  const activePsi = psi[psiStrategy] || psi.mobile || psi.desktop;
  const crawlInfo = data?.crawlInfo || {};
  const sslInfo = data?.rawData?.sslInfo || data?.sslInfo || null;
  const pages = data?.pages || [];

  const categories = data?.categories || [];
  const healthChecks = data?.healthChecks || [];

  const filteredIssues =
    severityFilter === 'all'
      ? issues
      : issues.filter((i) => i.priority === severityFilter);

  const criticalCount = data?.issues?.critical || 0;
  const warningCount = data?.issues?.warnings || 0;
  const passedCount = data?.issues?.passed || 0;

  // ── Render ──
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* ─── Header Card ─── */}
        <div style={styles.card}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 20
            }}>
            <div>
              <h1
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: '#1e293b',
                  margin: 0
                }}>
                SEO Audit Report
              </h1>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 6
                }}>
                <Globe size={16} color="#64748b" />
                <span style={{ color: '#64748b', fontSize: 16 }}>
                  {data?.url || data?.target || 'Unknown'}
                </span>
                {crawlInfo.pagesCrawled && (
                  <span style={styles.badge}>
                    {crawlInfo.pagesCrawled} pages crawled
                  </span>
                )}
              </div>
              {crawlInfo.crawlStart && (
                <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>
                  Audit date:{' '}
                  {new Date(crawlInfo.crawlStart).toLocaleDateString()}
                </p>
              )}
            </div>
            <div style={styles.scoreRing(scores.overall || 0)}>
              <span
                style={{
                  fontSize: 36,
                  fontWeight: 800,
                  color: getScoreColor(scores.overall || 0)
                }}>
                {scores.overall || 0}
              </span>
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
                / 100
              </span>
            </div>
          </div>

          {/* Issue counters */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12,
              marginTop: 24
            }}>
            <div style={styles.counterBox('#fef2f2', '#dc2626')}>
              <div style={{ fontSize: 28, fontWeight: 800 }}>
                {criticalCount}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Critical</div>
            </div>
            <div style={styles.counterBox('#fefce8', '#ca8a04')}>
              <div style={{ fontSize: 28, fontWeight: 800 }}>
                {warningCount}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Warnings</div>
            </div>
            <div style={styles.counterBox('#f0fdf4', '#16a34a')}>
              <div style={{ fontSize: 28, fontWeight: 800 }}>{passedCount}</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Passed</div>
            </div>
          </div>
        </div>

        {/* ─── Category Scores ─── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 16,
            marginBottom: 24
          }}>
          {categories.map((cat) => (
            <div
              key={cat.name}
              style={{
                ...styles.card,
                textAlign: 'center',
                padding: '24px 16px'
              }}>
              <div style={{ color: getScoreColor(cat.score), marginBottom: 8 }}>
                {getCategoryIcon(cat.name)}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: '#94a3b8',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 6
                }}>
                {cat.name}
              </div>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: getScoreColor(cat.score)
                }}>
                {cat.score}
              </div>
              {/* Progress bar */}
              <div
                style={{
                  height: 4,
                  background: '#e2e8f0',
                  borderRadius: 4,
                  marginTop: 10,
                  overflow: 'hidden'
                }}>
                <div
                  style={{
                    height: '100%',
                    width: `${cat.score}%`,
                    background: getScoreColor(cat.score),
                    borderRadius: 4,
                    transition: 'width 0.5s ease'
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* ─── Health Checks ─── */}
        <div style={{ ...styles.card, marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 16
            }}>
            <div style={styles.iconBox('#16a34a')}>
              <Shield size={20} color="white" />
            </div>
            <div>
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#1e293b',
                  margin: 0
                }}>
                SEO Health Checks
              </h2>
              <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>
                {healthChecks.filter((c) => c.passed).length} of{' '}
                {healthChecks.length} checks passed
              </p>
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 10
            }}>
            {healthChecks.map((check) => (
              <div
                key={check.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: 14,
                  borderRadius: 12,
                  border: `1px solid ${check.passed ? '#bbf7d0' : '#fecaca'}`,
                  background: check.passed ? '#f0fdf4' : '#fef2f2'
                }}>
                <div style={{ flexShrink: 0, marginTop: 2 }}>
                  {check.passed ? (
                    <CheckCircle size={18} color="#16a34a" />
                  ) : (
                    <XCircle size={18} color="#dc2626" />
                  )}
                </div>
                <div>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: check.passed ? '#166534' : '#991b1b'
                      }}>
                      {check.label}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '2px 6px',
                        borderRadius: 10,
                        background: check.passed ? '#bbf7d0' : '#fecaca',
                        color: check.passed ? '#166534' : '#991b1b'
                      }}>
                      {check.passed ? 'PASS' : 'FAIL'}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: 12,
                      color: '#64748b',
                      margin: '4px 0 0',
                      lineHeight: 1.5
                    }}>
                    <strong>Why it matters:</strong> {check.why}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ─── PSI Section ─── */}
        {(psi.mobile || psi.desktop) && (
          <div style={{ ...styles.card, marginBottom: 24 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={styles.iconBox('#3766a8')}>
                  <Zap size={20} color="white" />
                </div>
                <div>
                  <h2
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: '#1e293b',
                      margin: 0
                    }}>
                    PageSpeed Insights
                  </h2>
                  <p style={{ fontSize: 14, color: '#94a3b8', margin: 0 }}>
                    Performance analysis by Google Lighthouse
                  </p>
                </div>
              </div>
              <div style={styles.toggleGroup}>
                {['mobile', 'desktop'].map((s) => (
                  <button
                    key={s}
                    onClick={() => setPsiStrategy(s)}
                    style={styles.toggleButton(psiStrategy === s)}>
                    {s === 'mobile' ? '📱 Mobile' : '🖥️ Desktop'}
                  </button>
                ))}
              </div>
            </div>
            {activePsi?.scores ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: 12
                }}>
                {Object.entries(activePsi.scores).map(([key, val]) => {
                  const score =
                    typeof val === 'number' && val <= 1
                      ? Math.round(val * 100)
                      : Math.round(val);
                  return (
                    <div
                      key={key}
                      style={{
                        padding: 16,
                        borderRadius: 12,
                        border: '2px solid #e2e8f0',
                        textAlign: 'center',
                        background: getScoreBg(score)
                      }}>
                      <div
                        style={{
                          fontSize: 11,
                          color: '#94a3b8',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          marginBottom: 4
                        }}>
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                      <div
                        style={{
                          fontSize: 28,
                          fontWeight: 800,
                          color: getScoreColor(score)
                        }}>
                        {score}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                {psiStrategy === 'mobile' ? 'Mobile' : 'Desktop'} scores not
                available.
              </p>
            )}

            {/* Core Web Vitals */}
            {activePsi?.coreWebVitals && (
              <div style={{ marginTop: 20 }}>
                <h4
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: 12
                  }}>
                  Core Web Vitals
                </h4>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 12
                  }}>
                  {Object.entries(activePsi.coreWebVitals).map(([key, val]) => (
                    <div
                      key={key}
                      style={{
                        padding: 14,
                        borderRadius: 10,
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#94a3b8',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          marginBottom: 4
                        }}>
                        {key}
                      </div>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: '#1e293b'
                        }}>
                        {String(val)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── SSL Info ─── */}
        {sslInfo && (
          <div style={{ ...styles.card, marginBottom: 24 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 16
              }}>
              <div style={styles.iconBox('#16a34a')}>
                <Lock size={20} color="white" />
              </div>
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#1e293b',
                  margin: 0
                }}>
                SSL Certificate
              </h2>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 12
              }}>
              {sslInfo.valid_certificate !== undefined && (
                <div style={styles.metricBox}>
                  <span style={{ color: '#94a3b8', fontSize: 13 }}>
                    Valid Certificate
                  </span>
                  <span
                    style={{
                      fontWeight: 700,
                      color: sslInfo.valid_certificate ? '#16a34a' : '#dc2626'
                    }}>
                    {sslInfo.valid_certificate ? '✓ Yes' : '✗ No'}
                  </span>
                </div>
              )}
              {sslInfo.certificate_issuer && (
                <div style={styles.metricBox}>
                  <span style={{ color: '#94a3b8', fontSize: 13 }}>Issuer</span>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>
                    {sslInfo.certificate_issuer}
                  </span>
                </div>
              )}
              {sslInfo.certificate_expiration_date && (
                <div style={styles.metricBox}>
                  <span style={{ color: '#94a3b8', fontSize: 13 }}>
                    Expires
                  </span>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>
                    {sslInfo.certificate_expiration_date}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Issues / Recommendations ─── */}
        {issues.length > 0 && (
          <div style={{ ...styles.card, marginBottom: 24 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={styles.iconBox('#eab308')}>
                  <AlertTriangle size={20} color="white" />
                </div>
                <div>
                  <h2
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: '#1e293b',
                      margin: 0
                    }}>
                    Issues & Recommendations ({issues.length})
                  </h2>
                  <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>
                    Actionable items to improve your SEO score
                  </p>
                </div>
              </div>
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                style={styles.select}>
                <option value="all">All Severity</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="warning">Warning</option>
                <option value="low">Low</option>
                <option value="info">Info</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredIssues.map((issue, idx) => {
                const ps = getPriorityStyle(issue.priority);
                const isExpanded = expandedIssues[idx];
                return (
                  <div
                    key={idx}
                    style={{
                      padding: 16,
                      borderRadius: 12,
                      border: `1px solid ${ps.border}`,
                      background: ps.bg,
                      cursor: 'pointer'
                    }}
                    onClick={() =>
                      setExpandedIssues((prev) => ({
                        ...prev,
                        [idx]: !prev[idx]
                      }))
                    }>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          flex: 1
                        }}>
                        <span
                          style={{
                            padding: '3px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            background: 'white',
                            color: ps.text,
                            textTransform: 'capitalize',
                            border: `1px solid ${ps.border}`
                          }}>
                          {issue.priority}
                        </span>
                        <span
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: '#1e293b'
                          }}>
                          {issue.title}
                        </span>
                        {issue.count > 0 && (
                          <span
                            style={{
                              fontSize: 12,
                              color: '#94a3b8',
                              padding: '2px 8px',
                              background: 'white',
                              borderRadius: 4,
                              border: '1px solid #e2e8f0'
                            }}>
                            {issue.count} {issue.count === 1 ? 'item' : 'items'}
                          </span>
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronUp size={18} color="#94a3b8" />
                      ) : (
                        <ChevronDown size={18} color="#94a3b8" />
                      )}
                    </div>
                    {isExpanded && (
                      <div
                        style={{
                          marginTop: 12,
                          paddingTop: 12,
                          borderTop: `1px solid ${ps.border}`
                        }}>
                        <p
                          style={{
                            fontSize: 14,
                            color: '#475569',
                            margin: '0 0 8px 0',
                            lineHeight: 1.5
                          }}>
                          {issue.description}
                        </p>
                        {issue.fix && (
                          <p
                            style={{
                              fontSize: 13,
                              color: '#3766a8',
                              margin: 0,
                              lineHeight: 1.5
                            }}>
                            💡 <strong>How to fix:</strong> {issue.fix}
                          </p>
                        )}
                        {WHY_IT_MATTERS[issue.title] && (
                          <p
                            style={{
                              fontSize: 13,
                              color: '#2563eb',
                              margin: '8px 0 0 0',
                              lineHeight: 1.5,
                              padding: '8px 12px',
                              background: '#eff6ff',
                              borderRadius: 8,
                              border: '1px solid #bfdbfe'
                            }}>
                            📌 <strong>Why it matters:</strong>{' '}
                            {WHY_IT_MATTERS[issue.title]}
                          </p>
                        )}
                        {issue.items && issue.items.length > 0 && (
                          <div style={{ marginTop: 12 }}>
                            <p
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#1e293b',
                                margin: '0 0 6px 0'
                              }}>
                              Affected items ({issue.items.length}):
                            </p>
                            <ul
                              style={{
                                margin: 0,
                                paddingLeft: 20,
                                fontSize: 13,
                                color: '#475569',
                                lineHeight: 1.5
                              }}>
                              {issue.items.slice(0, 10).map((item, iIdx) => (
                                <li key={iIdx}>
                                  {typeof item === 'string'
                                    ? item
                                    : item.url ||
                                      item.label ||
                                      JSON.stringify(item)}
                                </li>
                              ))}
                              {issue.items.length > 10 && (
                                <li
                                  style={{
                                    color: '#94a3b8',
                                    fontStyle: 'italic'
                                  }}>
                                  ...and {issue.items.length - 10} more
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── Pages Breakdown ─── */}
        {pages.length > 0 && (
          <div style={{ ...styles.card, marginBottom: 24 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 16
              }}>
              <div style={styles.iconBox('#3766a8')}>
                <FileText size={20} color="white" />
              </div>
              <div>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#1e293b',
                    margin: 0
                  }}>
                  Pages Breakdown
                </h2>
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>
                  Top {Math.min(pages.length, 20)} pages analyzed
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pages.slice(0, 20).map((page, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '12px 16px',
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#1e293b',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                      {page.url || page.resource}
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginLeft: 12
                    }}>
                    <span
                      style={{
                        ...styles.tagBadge,
                        color: page.statusCode === 200 ? '#16a34a' : '#dc2626',
                        background:
                          page.statusCode === 200 ? '#f0fdf4' : '#fef2f2'
                      }}>
                      {page.statusCode || '—'}
                    </span>
                    {page.onpageScore !== undefined && (
                      <span
                        style={{
                          ...styles.tagBadge,
                          color: getScoreColor(page.onpageScore),
                          background: getScoreBg(page.onpageScore)
                        }}>
                        Score: {Math.round(page.onpageScore)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Footer ─── */}
        <div
          style={{
            textAlign: 'center',
            padding: '32px 0',
            color: '#94a3b8',
            fontSize: 13
          }}>
          <p style={{ margin: 0 }}>
            Report generated by{' '}
            <strong style={{ color: '#3766a8' }}>AdlerLocal</strong> SEO Audit
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 12 }}>
            Report ID: {reportId}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%)',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '32px 16px'
  },
  container: {
    maxWidth: 960,
    margin: '0 auto'
  },
  centerPage: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8fafc',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    background: 'white',
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
    marginBottom: 0
  },
  badge: {
    padding: '3px 10px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    background: '#f1f5f9',
    color: '#64748b',
    border: '1px solid #e2e8f0'
  },
  scoreRing: (score) => ({
    width: 100,
    height: 100,
    borderRadius: '50%',
    border: `5px solid ${getScoreColor(score)}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    flexShrink: 0,
    background: getScoreBg(score)
  }),
  counterBox: (bg, color) => ({
    padding: '16px 12px',
    borderRadius: 12,
    background: bg,
    color: color,
    textAlign: 'center'
  }),
  iconBox: (bg) => ({
    width: 40,
    height: 40,
    borderRadius: 10,
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  }),
  toggleGroup: {
    display: 'flex',
    gap: 4,
    background: '#f1f5f9',
    borderRadius: 8,
    padding: 4
  },
  toggleButton: (active) => ({
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 600,
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    background: active ? 'white' : 'transparent',
    color: active ? '#1e293b' : '#94a3b8',
    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
    transition: 'all 0.15s ease'
  }),
  select: {
    padding: '6px 12px',
    fontSize: 13,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    background: 'white',
    color: '#1e293b',
    cursor: 'pointer',
    fontWeight: 500
  },
  metricBox: {
    padding: '12px 16px',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  tagBadge: {
    padding: '3px 10px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    border: '1px solid transparent'
  }
};

export default SeoPublicReport;
